import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Lock, Globe, Shuffle } from "lucide-react";
import { Reveal } from "./Reveal";
import { PhoneFrame } from "./PhoneFrame";
import { demoList } from "../data/demos";

const modeIcon = { publica: Globe, privada: Lock, mixta: Shuffle };

export const Examples = ({ onOpenDemo }) => {
  const [active, setActive] = useState("boda");
  const demo = demoList.find((d) => d.key === active);
  const Icon = modeIcon[demo.mode];

  return (
    <section id="ejemplos" data-testid="examples-section" className="bg-[#FAFBFD] py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-600">Ejemplos interactivos</p>
          <h2
            className="mt-3 max-w-2xl text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Mirá cómo luce una invitación Plus
          </h2>
          <p className="mt-4 max-w-xl text-base text-slate-600">
            Tres ejemplos con datos ficticios de demostración. Abrí cada uno y recorrelo como lo haría tu invitado.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="examples-tabs" className="mt-8 flex flex-wrap gap-2">
            {demoList.map((d) => (
              <button
                key={d.key}
                data-testid={`example-tab-${d.key}`}
                onClick={() => setActive(d.key)}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                  active === d.key
                    ? "border-[#4F5BD5] bg-[#4F5BD5] text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-700"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <AnimatePresence mode="wait">
            <motion.div
              key={demo.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                data-testid="interactive-phone-frame"
                onClick={() => onOpenDemo(demo.key)}
                aria-label={`Abrir demo de ${demo.label}`}
                className="group block transition-transform duration-500 hover:-translate-y-2"
              >
                <PhoneFrame>
                  <div className="relative h-full w-full">
                    <img src={demo.cover} alt={`Invitación de demostración: ${demo.label}`} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 text-left text-white">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-white/75">{demo.subtitle}</p>
                      <p className="mt-1 text-2xl font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {demo.title}
                      </p>
                      <p className="mt-2 text-xs text-white/80">{demo.dateLabel}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-medium backdrop-blur-sm transition-colors duration-300 group-hover:bg-[#4F5BD5]">
                        Abrir invitación
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </PhoneFrame>
              </button>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${demo.key}-info`}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="order-first lg:order-none"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                <Icon className="h-3.5 w-3.5" />
                {demo.modeLabel}
              </span>
              <h3 className="mt-4 text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {demo.title}
              </h3>
              <p className="mt-2 text-sm font-medium uppercase tracking-wide text-slate-400">Contenido de demostración</p>
              <ul className="mt-6 space-y-3">
                {demo.itinerary.slice(0, 4).map((it) => (
                  <li key={it.time} className="flex items-baseline gap-4 border-b border-slate-100 pb-3">
                    <span className="w-12 shrink-0 text-sm font-semibold text-indigo-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {it.time}
                    </span>
                    <span className="text-sm text-slate-700">{it.title}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`example-open-${demo.key}`}
                onClick={() => onOpenDemo(demo.key)}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4F5BD5] px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#3B46BC]"
              >
                Recorrer esta invitación
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
